<template>
  <!-- Transaction Data Example -->
  <div class="d-flex align-items-center">
    <p class="mb-0">
      <!-- Currently using this bulb to quickly set splurges as the transaction's category, since is the most used. -->
      <IconBulb
        v-if="!txnAlreadyCategorized"
        size="20"
        class="text-success"
        style="cursor: pointer"
        @click="handleTxnSelectionWithDefaultCategory"
      />
    </p>
    <div
      class="px-1 py-2 mx-1 my-2 border rounded"
      :style="
        txnAlreadyCategorized
          ? 'background-color: #e9ecef; border-color: #ced4da;'
          : 'background-color: white;'
      "
      style="border: 2px solid black; cursor: pointer"
      @click="handleTxnSelection"
    >
      <span
        v-if="txnOverrideAmount !== null"
        :class="txn.amount < 0 ? 'text-danger' : 'text-success'"
      >
        <strong
          >{{ formatCurrencyDollarSign(txnOverrideAmount) }}
          <small>({{ formatCurrencyDollarSign(txn.amount) }})</small></strong
        >
      </span>
      <strong v-else :class="txn.amount < 0 ? 'text-danger' : 'text-success'">{{
        formatCurrencyDollarSign(txn.amount)
      }}</strong>
      {{ txn.description }} on
      {{ txn.date }}
      <BBadge variant="dark">{{ txn.institution_name }}</BBadge>
    </div>
    <p class="mb-0">
      <IconClipboardText
        size="20"
        class="ms-2"
        :class="txn?.note ? 'text-info' : 'text-muted'"
        style="cursor: pointer"
        v-b-tooltip.hover
        :title="`${txn?.note}`"
      />
    </p>
    <p v-if="showDeleteTransactionAction" class="mb-0 ms-2">
      <IconTrash
        v-if="!confirmDeletion"
        size="20"
        class="ms-2 text-danger"
        style="cursor: pointer"
        @click="triggerDeletionConfirmation"
      />
      <IconTrashXFilled
        v-else
        size="20"
        class="ms-2 text-danger"
        style="cursor: pointer"
        @click="handleTransactionDeletion"
      />
    </p>
  </div>
  <CreateAssignmentModal
    v-if="thisMonday !== null"
    ref="createAssignmentModal"
    :txn="txn"
    :weeksAsOpts="weeksAsOpts"
    :thisMonday="thisMonday"
    :spendCategoriesOpts="spendCategoriesOpts"
    v-model="createAssignmentModal"
    @assignationSaved="handleAssignationSaved"
  />
  <EditTransactionModal :txn="txn" v-model="editTrsxnModal" />
</template>
<script>
import CreateAssignmentModal from "@/components/Transactions/CreateAssignmentModal.vue";
import EditTransactionModal from "@/components/Transactions/EditTransactionModal.vue";
import numberFormatMixin from "@/mixins/numberFormatMixin";
import {
  IconBulb,
  IconClipboardText,
  IconTrash,
  IconTrashXFilled,
} from "@tabler/icons-vue";
import { BBadge, vBTooltip } from "bootstrap-vue-next";
import {
  spendingCategory as spendingCategoryNs,
  weeklyOutlooks as weeklyOutlooksNs,
} from "@/store/namespaces";
import { mapGetters } from "vuex";

export default {
  name: "TransactionsListItemView",
  props: {
    txn: {
      type: Object,
      required: true,
    },
    showDeleteTransactionAction: {
      type: Boolean,
      default: true,
    },
    weeklyOutlookDisplay: {
      type: Boolean,
      default: false,
    },
  },
  directives: {
    BTooltip: vBTooltip,
  },
  emits: ["deleteTransaction"],
  mixins: [numberFormatMixin],
  components: {
    BBadge,
    CreateAssignmentModal,
    EditTransactionModal,
    IconBulb,
    IconClipboardText,
    IconTrash,
    IconTrashXFilled,
  },
  data() {
    return {
      confirmDeletion: false,
      createAssignmentModal: false,
      editTrsxnModal: false,
      thisMonday: null, // Will hold the date string for this week's Monday (e.g., "2026-04-13")
      hasBeenCategorizedInFrontend: false, // This is a frontend-only flag to track if the transaction has been categorized during this session. It will be reset on page refresh. We use this to immediately reflect categorization changes in the UI without waiting for a refetch from the backend.
    };
  },
  computed: {
    ...mapGetters({
      vuexWeeks: `${weeklyOutlooksNs}/getWeeklyOutlooks`,
    }),
    // Display a "special" amount if the transaction has a snapshot category with a fixed_amount that differs from the transaction's original amount. This is useful for cases where the transaction has been categorized and the fixed_amount is different from the original amount.
    txnOverrideAmount() {
      if (this.weeklyOutlookDisplay == false) return null;

      // Get the fixed_amount from the snapshot category whose spending_category_uid is equal to splurges, if any. If none, return null.
      const splurgesFixedAmount = this.txn?.snapshot_categories?.find(
        (cat) => cat.spending_category_uid === "splurges"
      )?.fixed_amount;

      return splurgesFixedAmount == this.txn.amount
        ? null
        : splurgesFixedAmount;
    },
    txnAlreadyCategorized() {
      return (
        (this.txn.snapshot_categories &&
          this.txn.snapshot_categories.length > 0) ||
        this.hasBeenCategorizedInFrontend
      );
    },
    weeksAsOpts() {
      if (!this.vuexWeeks) return [];

      return [
        { value: null, text: "— Select a week —", disabled: true },
        ...this.vuexWeeks.map((week) => ({
          value: week.weekly_outlook,
          text:
            week.weekly_outlook === this.thisMonday
              ? `${week.weekly_outlook} (This Week)`
              : week.weekly_outlook,
        })),
      ];
    },
    spendCategoriesOpts() {
      const cats =
        this.$store.getters[`${spendingCategoryNs}/getSpendingCategories`];
      if (!cats) {
        return [];
      }

      return [
        { value: null, text: "— Select a category —", disabled: true },
        ...cats.map((cat) => ({
          value: cat.item,
          text: cat.item.name,
        })),
      ];
    },
  },
  mounted() {
    if (!this.vuexWeeks) {
      console.error("Weeks data is required for TransactionsListItemView");
    }

    // Calculate this week's monday in format YYYY-MM-DD and set it as the default selected week
    const monday = new Date();
    const dayIdx = monday.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    monday.setDate(monday.getDate() - (dayIdx === 0 ? 6 : dayIdx - 1)); // Adjust to get to Monday (if Sunday, go back 6 days)
    const year = monday.getFullYear();
    const month = String(monday.getMonth() + 1).padStart(2, "0");
    const date = String(monday.getDate()).padStart(2, "0");
    const formattedMonday = `${year}-${month}-${date}`;
    this.thisMonday = formattedMonday;
  },
  methods: {
    handleTxnSelection() {
      if (this.txnAlreadyCategorized) {
        this.editTrsxnModal = true;
        return;
      }

      this.createAssignmentModal = true;
    },
    handleAssignationSaved() {
      this.hasBeenCategorizedInFrontend = true;
    },
    handleTxnSelectionWithDefaultCategory() {
      if (this.txnAlreadyCategorized) return; // Just to be safe
      this.handleTxnSelection();
      this.$refs.createAssignmentModal.assignSplurgesAsDefault();
    },
    triggerDeletionConfirmation() {
      this.confirmDeletion = true;
      setTimeout(() => {
        this.confirmDeletion = false;
      }, 3000);
    },
    handleTransactionDeletion() {
      this.$emit("deleteTransaction", this.txn.transaction_uid);
      this.confirmDeletion = false;
    },
  },
};
</script>
